import React, { createContext, useContext, useState, useCallback } from 'react';
import { Config, OperatingScenario } from '../types/config';

/**
 * Default configuration values for both operating scenarios
 * 
 * These values represent the initial state of the system configuration for both
 * the current and expanded operating scenarios. They define the energy production, 
 * consumption, and operational parameters for the solar dashboard system.
 */
const defaultConfig: Config = {
  // Current scenario: 6 chargers operating 14 hours per day
  current: {
    dailyConsumption: 13.44,       // Total daily energy consumption in MWh
    solarProduction: 12.15,        // Total daily solar energy production in MWh
    directSolarUsage: 7.56,        // Solar energy used directly (not stored) in MWh
    excessSolar: 4.59,             // Solar energy production exceeding direct usage in MWh
    gridNeeded: 5.88,              // Grid energy required without battery in MWh
    batteryUsable: 4.59,           // Usable battery storage capacity in MWh (matching excess solar)
    gridNeededWithBattery: 1.29,   // Grid energy required with battery in MWh
    operatingHours: 14,            // Daily operating hours of charging stations
    solarHours: 4.5,               // Daily hours of peak solar production
    chartCapacity: 960,            // Maximum power capacity for charts in kW
  },
  
  // Expanded scenario: 12 chargers operating 24 hours per day
  expanded: {
    dailyConsumption: 46.08,       // Total daily energy consumption in MWh
    solarProduction: 24.3,         // Total daily solar energy production in MWh
    directSolarUsage: 14.58,       // Solar energy used directly (not stored) in MWh
    excessSolar: 9.72,             // Solar energy production exceeding direct usage in MWh
    gridNeeded: 31.5,              // Grid energy required without battery in MWh
    batteryUsable: 9.0,            // Usable battery storage capacity in MWh (limited by battery capacity)
    gridNeededWithBattery: 22.5,   // Grid energy required with battery in MWh
    operatingHours: 24,            // Daily operating hours of charging stations
    solarHours: 4.5,               // Daily hours of peak solar production
    chartCapacity: 1920,           // Maximum power capacity for charts in kW
  }
};

/**
 * ConfigContext type definition
 * 
 * Defines the shape of the context object that will be provided to consumers,
 * including the current configuration state and methods to update it.
 */
interface ConfigContextType {
  config: Config;                                 // Current configuration state
  operatingScenario: OperatingScenario;           // Current active scenario ('current' or 'expanded')
  setOperatingScenario: (scenario: OperatingScenario) => void;  // Method to switch scenarios
  updateConfig: (scenario: OperatingScenario, name: string, value: number) => void;  // Method to update configuration
}

/**
 * Create ConfigContext with default values
 * 
 * The default values are used when a component consumes the context without
 * being wrapped in a ConfigProvider.
 */
export const ConfigContext = createContext<ConfigContextType>({
  config: defaultConfig,
  operatingScenario: 'current',
  setOperatingScenario: () => {},
  updateConfig: () => {}
});

/**
 * ConfigProvider component
 * 
 * This component manages the global state for the system configuration, including
 * both operating scenarios and the currently active scenario. It provides methods
 * to update configuration values and switch between scenarios.
 * 
 * The ConfigProvider automatically handles dependency relationships between configuration
 * parameters, ensuring that derived values are updated consistently.
 * 
 * Usage:
 * ```tsx
 * <ConfigProvider>
 *   <App />
 * </ConfigProvider>
 * ```
 * 
 * @param props - Component props with children
 * @returns Provider component that makes configuration state available to all children
 */
export const ConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // State for configuration and active scenario
  const [config, setConfig] = useState<Config>(defaultConfig);
  const [operatingScenario, setOperatingScenario] = useState<OperatingScenario>('current');
  
  /**
   * Update configuration parameter with automatic recalculation of dependencies
   * 
   * This method updates a single configuration parameter for the specified scenario
   * and automatically recalculates any dependent values to maintain consistency.
   * For example, when solar production changes, excess solar is recalculated.
   * 
   * @param scenario - The operating scenario to update ('current' or 'expanded')
   * @param name - The configuration parameter to update
   * @param value - The new parameter value
   */
  const updateConfig = useCallback((
    scenario: OperatingScenario, 
    name: string, 
    value: number
  ) => {
    setConfig(prevConfig => {
      // Create updated config object with the new parameter value
      const updatedConfig = {
        ...prevConfig,
        [scenario]: {
          ...prevConfig[scenario],
          [name]: value
        }
      };
      
      // Recalculate dependent values based on what was changed
      if (name === 'solarProduction' || name === 'directSolarUsage') {
        // Calculate new excess solar based on the updated parameter
        const newExcessSolar = name === 'solarProduction' 
          ? value - prevConfig[scenario].directSolarUsage  // If solar production changed
          : prevConfig[scenario].solarProduction - value;  // If direct solar usage changed
          
        // Update excess solar in the config
        updatedConfig[scenario].excessSolar = newExcessSolar;
      }
      
      // Return the updated configuration
      return updatedConfig;
    });
  }, []);
  
  // Provide context value to children
  return (
    <ConfigContext.Provider value={{ 
      config, 
      operatingScenario, 
      setOperatingScenario, 
      updateConfig 
    }}>
      {children}
    </ConfigContext.Provider>
  );
};

/**
 * Custom hook for using the config context
 * 
 * This hook provides convenient access to the configuration context in functional components.
 * It handles the context consumption and throws an error if used outside a ConfigProvider.
 * 
 * Usage:
 * ```tsx
 * const { config, operatingScenario, setOperatingScenario, updateConfig } = useConfig();
 * ```
 * 
 * @returns The current configuration context value
 */
export const useConfig = () => useContext(ConfigContext);